import { useCallback } from 'react';

import { useObjectMemo } from '@client/hooks/use-object.hooks';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import type { BuildProposalAppendActionPatchOptions } from '@shared/helpers/actions/apply-changes-to-proposal.helpers';
import {
  getApplyProjectChangesExpressionOverrideValidationError,
  getApplyProjectChangesValidationErrorMessage,
  logApplyProjectChangesValidationError,
} from '@shared/helpers/actions/changes-validation';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import amplitudeLog from '@client/amplitude';
import { getProject, useUpdateProject } from './project.hook';
import { appliedChangesEvents } from '@shared/helpers/project-events/applied-changes-events.helpers';
import { getSelectedVersion } from '@client/store/ui';
import type { ChangesProjectUpdate } from './project.hook';
import type { IUpdateProjectOptions } from './project-state.model';
import type { ISnackbarOptions } from '@client/hooks/use-snackbar.hooks';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  useUndo,
  useWaitForUndoStack,
} from '@client/hooks/use-undo-redo.hooks';
import { guards } from '@shared/helpers/typeguards.helpers';
import type { ConfirmOptions } from 'material-ui-confirm';
import type { Project } from '@shared/models/project.model';
import {
  getApplyProjectChangesSnackbarCallbacks,
  mergeApplyProjectUpdateLifecycle,
  type ApplyProjectChangesSnackbarOptions,
} from './apply-project-changes-snackbar.helpers';

export type ApplyProjectChangesConfirmOptions = string | ConfirmOptions;

export type ApplyProjectChangesOptions = ISnackbarOptions &
  IUpdateProjectOptions & {
    /** Passed to {@link buildProposalAppendActionPatch} when a proposal is active (DEV-3313). */
    proposalAppend?: BuildProposalAppendActionPatchOptions;
    /** When provided, asks the user to confirm before applying the changes. A string becomes the dialog description. */
    confirm?: ApplyProjectChangesConfirmOptions;
    /** When provided, logs an Amplitude event after the changes have been applied successfully. */
    amplitude?:
      | string
      | {
          eventType: string;
          eventProperties?: Record<string, unknown>;
        };
    /** When `true`, validation errors are shown in a snackbar.
     * When a string, it is used as the snackbar message.
     * Default is `true`. */
    showErrorsInSnackbar?: boolean | string;
    /** Optional snackbar after apply; use {@link ApplyProjectChangesSnackbarOptions} `type` for success vs undo. */
    snackbar?: ApplyProjectChangesSnackbarOptions;
  } & Pick<ChangesProjectUpdate, 'activeVersionId'>;

export type {
  ApplyProjectChangesSnackbarOptions,
  ApplyProjectChangesSnackbarTiming,
} from './apply-project-changes-snackbar.helpers';

/**
 * Outcome of {@link useApplyProjectChanges}: always includes the current or updated project.
 * {@link ApplyProjectChangesResult.cancelled} is set when the user dismissed a confirm dialog.
 * {@link ApplyProjectChangesResult.error} is set when lightweight expression override checks fail.
 */
export type ApplyProjectChangesResult = {
  /**
   * The project after the changes have been applied. OR unmodified if there were no changes, cancelled, or errors.
   */
  project: Project;
  /**
   * When set, the changes were not applied due to a validation error.
   * Note: not all validation errors are caught here, some are caught during enrichment.
   * When {@link ApplyProjectChangesOptions.showErrorsInSnackbar} was enabled for this call,
   * the error was also shown in a snackbar.
   */
  error?: Error | string;
  /**
   * When `true`, the user dismissed a confirm dialog.
   */
  cancelled?: boolean;
};

/**
 * Applies {@link ProjectChange}[] either to the project baseline or to the selected proposal (new action per call).
 * Delegates to {@link applyProjectChangesWithProposalContext}; persists via {@link useUpdateProject}.
 *
 * Optionally guards the call behind a confirm dialog ({@link ApplyProjectChangesOptions.confirm})
 * and emits an Amplitude event after a successful update ({@link ApplyProjectChangesOptions.amplitude}).
 *
 * Runs {@link getApplyProjectChangesExpressionOverrideValidationError} before mutating the store for a
 * cheap min/max / basic solve pass only; resolves the building version from each change's element id
 * (same path index as the batch pipeline). Full validation happens during enrichment.
 *
 * When {@link ApplyProjectChangesOptions.showErrorsInSnackbar} is `true`, apply-layer validation
 * failures are shown with {@link useSnackbar}. When it is a string, that string is used as the
 * snackbar message instead of the error text.
 *
 * {@link ISnackbarOptions.logError}: when omitted or `true`, logs pre-flight
 * failures with {@link logApplyProjectChangesValidationError}, and nested update
 * failures through {@link useUpdateProject}. A min/max bound violation is not
 * logged: the snackbar already shows it, and `console.error` would be reported
 * to Sentry. Pass `false` to silence console output only.
 *
 * {@link ApplyProjectChangesOptions.snackbar}: optional feedback after apply. `type: 'success'` uses a success variant;
 * `type: 'undo'` adds an Undo action (global undo). By default the snackbar is shown on the optimistic local update;
 * use `timing: 'afterEnrich'` to show after enrichment. For `undo` + early timing, Undo waits until enrichment succeeds.
 */
export const useApplyProjectChanges = (
  options: ApplyProjectChangesOptions = {},
): ((
  changes: ProjectChange[],
  optionsOverrides?: ApplyProjectChangesOptions,
) => Promise<ApplyProjectChangesResult>) => {
  const updateProject = useUpdateProject();
  const isConfirmed = useIsConfirmed();
  const selectedProposalId = useSelectedProposalId();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const undo = useUndo();
  const waitForUndoStack = useWaitForUndoStack();
  const memoOptions = useObjectMemo(options);

  return useCallback(
    async (
      changes: ProjectChange[],
      optionsOverrides: ApplyProjectChangesOptions = {},
    ) => {
      // No-op when there are no changes
      const preProject = getProject();
      if (changes.length === 0) return { project: preProject };

      // Merge base options with callback overrides
      const merged = {
        ...memoOptions,
        ...optionsOverrides,
      };

      const {
        confirm: confirmOptions,
        amplitude,
        showErrorsInSnackbar = true,
        logError = true,
        snackbar: snackbarOptions,
        proposalAppend,
        enrichScope,
        // The hook owns these for the 'changes' path — pulled out so they don't
        // leak into the update as stale/duplicate fields. `skipEnrichment` stays
        // on `updateOptions` so cloned results (e.g. baseline-from-proposal) can
        // skip the worker when {@link Project.enriched} is already true.
        appliedChanges: _appliedChanges,
        enrichSelectedProposalId: _enrichSelectedProposalId,
        serverOnly: _serverOnly,
        ...updateOptions
      } = merged;

      // Ask for confirmation if needed
      if (confirmOptions && !(await isConfirmed(confirmOptions))) {
        return { project: getProject(), cancelled: true };
      }

      // Run validation checks before applying changes
      const validationError =
        getApplyProjectChangesExpressionOverrideValidationError(
          preProject,
          changes,
        );

      // If validation fails we should return early and maybe show a snackbar
      if (validationError) {
        const result = { project: preProject, error: validationError };
        if (logError) logApplyProjectChangesValidationError(validationError);
        const snackbarMessage = getApplyProjectChangesValidationErrorMessage(
          showErrorsInSnackbar,
          validationError,
        );
        if (snackbarMessage !== undefined) {
          enqueueSnackbar(snackbarMessage, { variant: 'error' });
        }
        return result;
      }

      const snackbarCallbacks = snackbarOptions
        ? getApplyProjectChangesSnackbarCallbacks({
            snackbar: snackbarOptions,
            enqueueSnackbar,
            closeSnackbar,
            undo,
            waitForUndoStack,
          })
        : {};

      const versionId = getSelectedVersion()?.id;
      const result = await updateProject({
        kind: 'changes',
        baseline: preProject,
        changes,
        eventsFromChanges: (before, after) =>
          appliedChangesEvents({
            before,
            after,
            changes,
            versionId,
            proposalId: selectedProposalId,
          }),
        selectedProposalId,
        proposalAppend,
        enrichScope,
        ...updateOptions,
        logError,
        ...snackbarCallbacks,
        ...mergeApplyProjectUpdateLifecycle(updateOptions, snackbarCallbacks),
      });

      // Swallowed persist/enrich errors return the pre-apply project by reference.
      if (result !== preProject) {
        logAmplitudeEvent(amplitude);
      }

      return { project: result };
    },
    [
      closeSnackbar,
      enqueueSnackbar,
      isConfirmed,
      memoOptions,
      selectedProposalId,
      undo,
      updateProject,
      waitForUndoStack,
    ],
  );
};

/**
 * Logs an Amplitude event if it should be logged.
 * @param amplitude - The amplitude options to log.
 */
const logAmplitudeEvent = (
  amplitude: ApplyProjectChangesOptions['amplitude'],
) => {
  if (!amplitude) return;
  const eventType = guards.is.string(amplitude)
    ? amplitude
    : amplitude.eventType;
  const eventProperties = guards.is.string(amplitude)
    ? undefined
    : amplitude.eventProperties;
  amplitudeLog(eventType, eventProperties);
};
